<script setup lang="ts">
import { officeSecondsInRange } from '@schwrz/shared';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import { taskDetailsToForTask } from '../../shared/lib/task-route';
import { useClientsStore } from '../../stores/clients';
import { useProjectsStore } from '../../stores/projects';
import { useWorkSessionsStore } from '../../stores/workSessions';

import ChamberTransportIcon from './ChamberTransportIcon.vue';

const workSessions = useWorkSessionsStore();
const projectsStore = useProjectsStore();
const clientsStore = useClientsStore();
const router = useRouter();

const interruptOpen = ref(false);
const interruptLabel = ref('');
const interruptInput = ref<HTMLInputElement | null>(null);
const fetchedAtMs = ref(Date.now());
const tick = ref(0);

let pollTimer: ReturnType<typeof setInterval> | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;

const main = computed(() => workSessions.state?.main ?? null);
const interrupt = computed(() => workSessions.state?.interrupt ?? null);
const mainPaused = computed(() => Boolean(main.value && interrupt.value));

function formatHms(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function elapsedOfficeSeconds(startedAt: string | null, running: boolean): string {
  if (!startedAt || !running) return '0:00:00';
  void tick.value;
  return formatHms(officeSecondsInRange(new Date(startedAt), new Date()));
}

const mainElapsed = computed(() => {
  if (!main.value) return '0:00:00';
  if (mainPaused.value) return 'пауза';
  return elapsedOfficeSeconds(main.value.startedAt, main.value.running);
});

const mainOfficeFrozen = computed(
  () => Boolean(main.value?.running && !mainPaused.value && !main.value.officeActive),
);

const interruptElapsed = computed(() => {
  if (!interrupt.value?.startedAt) return '0:00:00';
  void tick.value;
  return formatHms(officeSecondsInRange(new Date(interrupt.value.startedAt), new Date()));
});

const mainTaskLink = computed(() => {
  if (!main.value?.task) return null;
  return taskDetailsToForTask(main.value.task, {
    project: (id) => projectsStore.items.find((p) => p.id === id),
    company: (id) => clientsStore.items.find((c) => c.id === id),
  });
});

watch(
  () => workSessions.state,
  () => {
    fetchedAtMs.value = Date.now();
  },
);

onMounted(() => {
  void Promise.all([projectsStore.load(), clientsStore.load(), workSessions.refresh()]);
  pollTimer = setInterval(() => void workSessions.refresh(), 60_000);
  tickTimer = setInterval(() => {
    tick.value += 1;
  }, 1000);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
  if (tickTimer) clearInterval(tickTimer);
});

function openInterrupt(): void {
  interruptOpen.value = true;
  interruptLabel.value = '';
  requestAnimationFrame(() => interruptInput.value?.focus());
}

function closeInterrupt(): void {
  interruptOpen.value = false;
  interruptLabel.value = '';
}

async function submitInterrupt(): Promise<void> {
  const label = interruptLabel.value.trim();
  if (!label) return;
  closeInterrupt();
  await workSessions.startInterrupt(label);
}

function onInterruptKeydown(e: KeyboardEvent): void {
  if (e.key === 'Enter') {
    e.preventDefault();
    void submitInterrupt();
  } else if (e.key === 'Escape') {
    closeInterrupt();
  }
}
</script>

<template>
  <div
    class="chamber-widget"
    aria-label="Таймер задачи"
  >
    <template v-if="interruptOpen && main && !interrupt">
      <input
        ref="interruptInput"
        v-model="interruptLabel"
        type="text"
        class="chamber-widget__input"
        maxlength="200"
        placeholder="Прерывание…"
        @keydown="onInterruptKeydown"
      />
      <button
        type="button"
        class="btn btn--ghost btn--small btn--icon chamber-widget__btn"
        title="Начать прерывание"
        aria-label="Начать прерывание"
        @click="submitInterrupt"
      >
        <ChamberTransportIcon name="confirm" />
      </button>
      <button
        type="button"
        class="btn btn--ghost btn--small btn--icon chamber-widget__btn"
        title="Отмена"
        aria-label="Отмена"
        @click="closeInterrupt"
      >
        <ChamberTransportIcon name="cancel" />
      </button>
    </template>

    <template v-else-if="interrupt">
      <button
        type="button"
        class="btn btn--primary btn--small btn--icon chamber-widget__btn chamber-widget__btn--play"
        title="Вернуться к задаче"
        aria-label="Вернуться к задаче"
        @click="workSessions.endInterrupt()"
      >
        <ChamberTransportIcon name="play" />
      </button>
      <span
        class="chamber-widget__track"
        :title="interrupt.label"
      >
        <span class="chamber-widget__track-label">{{ interrupt.label }}</span>
        <span class="chamber-widget__time">{{ interruptElapsed }}</span>
      </span>
      <span
        v-if="main"
        class="chamber-widget__paused-hint"
        :title="main.task.title"
      >
        {{ main.task.title }}
      </span>
    </template>

    <template v-else-if="main">
      <button
        type="button"
        class="btn btn--ghost btn--small btn--icon chamber-widget__btn"
        title="Отложить"
        aria-label="Отложить"
        :disabled="mainPaused || !main.running"
        @click="workSessions.eject()"
      >
        <ChamberTransportIcon name="eject" />
      </button>
      <span class="chamber-widget__track">
        <RouterLink
          v-if="mainTaskLink"
          :to="mainTaskLink"
          class="chamber-widget__track-label chamber-widget__track-link"
          :title="main.task.title"
        >
          {{ main.task.title }}
        </RouterLink>
        <span
          v-else
          class="chamber-widget__track-label"
          :title="main.task.title"
        >{{ main.task.title }}</span>
        <span
          class="chamber-widget__time"
          :class="{
            'chamber-widget__time--paused': mainPaused,
            'chamber-widget__time--office-frozen': mainOfficeFrozen,
          }"
        >{{ mainElapsed }}</span>
      </span>
      <button
        type="button"
        class="btn btn--ghost btn--small btn--icon chamber-widget__btn"
        title="Готово"
        aria-label="Готово"
        :disabled="mainPaused || !main.running"
        @click="workSessions.fire()"
      >
        <ChamberTransportIcon name="done" />
      </button>
      <button
        type="button"
        class="btn btn--ghost btn--small btn--icon chamber-widget__btn"
        title="Прервать"
        aria-label="Прервать"
        :disabled="mainPaused"
        @click="openInterrupt"
      >
        <ChamberTransportIcon name="pause" />
      </button>
    </template>

    <template v-else-if="!workSessions.loading">
      <span class="chamber-widget__empty-label">Нет активных задач</span>
      <button
        type="button"
        class="btn btn--ghost btn--small btn--icon chamber-widget__btn"
        title="Очередь задач"
        aria-label="Открыть очередь задач"
        @click="router.push({ name: 'home' })"
      >
        <ChamberTransportIcon name="queue" />
      </button>
    </template>
  </div>
</template>

<style scoped>
.chamber-widget {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  min-height: 31px;
  padding: 0.2rem 0.35rem;
  border: 1px solid var(--divider);
  border-radius: 10px;
  background: var(--fill-subtle);
  font-size: 0.74rem;
  line-height: 1.1;
  white-space: nowrap;
  box-sizing: border-box;
}

.chamber-widget__track {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  padding: 0 0.1rem;
}

.chamber-widget__track-label {
  display: block;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.chamber-widget__track-link {
  color: inherit;
  text-decoration: none;
}

.chamber-widget__track-link:hover {
  text-decoration: underline;
}

.chamber-widget__time {
  font-variant-numeric: tabular-nums;
  font-family: var(--font-mono, monospace);
  font-size: 0.72rem;
  color: var(--muted);
  flex-shrink: 0;
}

.chamber-widget__time--paused {
  font-style: italic;
  text-transform: lowercase;
}

.chamber-widget__time--office-frozen {
  opacity: 0.75;
}

.chamber-widget__paused-hint {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
  color: var(--muted);
  font-size: 0.68rem;
  opacity: 0.8;
}

.chamber-widget__empty-label {
  color: var(--muted);
  padding: 0 0.2rem;
  font-size: 0.72rem;
}

.chamber-widget__btn {
  width: 26px;
  min-width: 26px;
  height: 26px;
  min-height: 26px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
}

.chamber-widget__btn--play :deep(.chamber-icon) {
  width: 15px;
  height: 15px;
}

.chamber-widget__btn--play {
  background: #6a9d48;
  border-color: #6a9d48;
  color: #fff;
}

.chamber-widget__btn--play:hover:not(:disabled) {
  background: #5a8539;
  border-color: #5a8539;
}

.chamber-widget__input {
  width: 9.5rem;
  max-width: 36vw;
  font-size: 0.74rem;
  padding: 0.18rem 0.45rem;
  border: 1px solid var(--divider);
  border-radius: 999px;
  background: var(--surface, #fff);
  color: inherit;
  min-width: 0;
  min-height: 24px;
}

.chamber-widget__input:focus {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
</style>
